import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaCube, FaCoins, FaServer, FaDollarSign, FaClock, FaSortNumericUpAlt, FaBolt, FaExchangeAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import AdsComponent from './AdsComponent';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import './Blocks.css';

const Blocks = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [blocksPerPage] = useState(6);
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    const fetchBlocks = async () => {
      try {
        const response = await axios.get('/api/blocks');
        setBlocks(response.data);
        setLoading(false);

        const blockCounts = {};
        response.data.forEach(block => {
          if (block.currency_name in blockCounts) {
            blockCounts[block.currency_name]++;
          } else {
            blockCounts[block.currency_name] = 1;
          }
        });

        const topCurrencies = Object.entries(blockCounts).sort((a, b) => b[1] - a[1]).slice(0, 20);
        setChartData({
          labels: topCurrencies.map(entry => entry[0]),
          datasets: [
            {
              label: 'Blocks Mined',
              data: topCurrencies.map(entry => entry[1]),
              backgroundColor: ['#4caf50', '#36a2eb', '#cc65fe', '#ffce56', '#4bc0c0','#ff6384', '#36a2eb', '#cc65fe', '#ffce56', '#4bc0c0'],
              borderWidth: 1,
            },
          ],
        });
      } catch (err) {
        setError('Error fetching blocks');
        setLoading(false);
      }
    };

    fetchBlocks();

    const interval = setInterval(() => {
      fetchBlocks();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const isValidNumber = (value) => {
    const number = parseFloat(value);
    return !isNaN(number) && isFinite(number);
  };

/*  const getCoinLogo = (symbol) => {
    return `/img/coins/${symbol.toLowerCase()}.webp`;
  };*/
  const getCoinLogo = (symbol) => {
    const sanitizedSymbol = symbol.split(/[-_]/)[0].toUpperCase();
    return `/img/coins/${sanitizedSymbol}.webp`;
  };

  const getPoolLogo = (poolName) => {
    if (!poolName) {
      return '/img/pools/default.webp';
    }
    const poolNameLower = poolName.toLowerCase().replace(/\s+/g, '');
    return `/img/pools/${poolNameLower}.webp`;
  };

  const filteredBlocks = blocks.filter((block) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      block.block_height.toString().includes(searchLower) || 
      block.currency_name.toLowerCase().includes(searchLower) || 
      block.currency_symbol.toLowerCase().includes(searchLower) || 
      block.pool_name.toLowerCase().includes(searchLower)
    );
  });

  const indexOfLastBlock = currentPage * blocksPerPage;
  const indexOfFirstBlock = indexOfLastBlock - blocksPerPage;
  const currentBlocks = filteredBlocks.slice(indexOfFirstBlock, indexOfLastBlock);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredBlocks.length / blocksPerPage); i++) {
    pageNumbers.push(i);
  }

  const maxPageDisplay = 5;
  const startPage = Math.max(1, currentPage - Math.floor(maxPageDisplay / 2));
  const endPage = Math.min(startPage + maxPageDisplay - 1, pageNumbers.length);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="Blocks-container">
      <h2><FaCube /> Latest Mined Blocks</h2>
      <p className="Blocks-info-text">
        In blockchain networks, a block is a record of valid transactions that are added to the chain in chronological order. This section showcases the most recent blocks mined across various cryptocurrency pools. 
        Each block contains key information such as the block height (its position in the blockchain), the cryptocurrency that was mined, the mining pool responsible for solving it, and the difficulty level of the computational problem solved. 
        If the mined cryptocurrency is traded on exchanges, you can also see the latest market price and the block’s total value converted to USD.
      </p>
      <div className="Blocks-search-wrapper">
        <input
          type="text"
          placeholder="Search by block height, coin, symbol, or pool"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="Blocks-search-input"
        />
      </div>
      <div className="sidebar-ad">
          <AdsComponent requiredSize="728x90" />
      </div>
      <div className="chart-container">
        <h3>Top Cryptocurrencies by Mined Blocks</h3>
        <Bar data={chartData} options={{ maintainAspectRatio: false }} height={200} />
      </div>

      <table className="Blocks-table">
        <thead>
          <tr>
            <th><FaCoins /> Coin</th>
            <th><FaServer /> Pool</th>
            <th>Symbol</th>
            <th><FaSortNumericUpAlt /> Block Height</th>
            <th><FaDollarSign /> Reward</th>
            <th><FaBolt /> Difficulty</th>
            <th><FaClock /> Timestamp</th>
            <th><FaExchangeAlt /> Exchanges</th>
          </tr>
        </thead>

        <tbody>
          {currentBlocks.map((block, index) => (
            <tr key={index}>
              <td>
                <div className="Blocks-logo-container">
                  <img
                    src={getCoinLogo(block.currency_symbol)}
                    alt={block.currency_name}
                    className="Blocks-coin-logo"
                    onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                  />
                  <div>{block.currency_name}</div>
                </div>
              </td>
              <td>
                <div className="Blocks-logo-container">
                  <img
                    src={getPoolLogo(block.pool_name)}
                    alt={block.pool_name}
                    className="Blocks-pool-logo"
                    onError={(e) => { e.target.src = '/img/pools/default.webp'; }}
                  />
                  <Link to={`/pool/${block.pool_name}`} className="Blocks-pool-link">
                    {block.pool_name}
                  </Link>
                </div>
              </td>
              <td>{block.currency_symbol}</td>
              <td>{block.block_height}</td>
              <td>{block.reward}</td>
              <td>{block.difficulty}</td>
              <td>{new Date(block.timestamp).toLocaleString()}</td>
              <td>
                {block.exchanges.length > 0 ? (
                  <table className="Exchanges-table">
                    <thead>
                      <tr>
                        <th>Exchange</th>
                        <th>Price (USDT)</th>
                        <th>Block Value (USDT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {block.exchanges.map((exchange, idx) => (
                        <tr key={idx}>
                          <td>{exchange.exchange_name}</td>
                          <td>{isValidNumber(exchange.lastPrice) ? parseFloat(exchange.lastPrice).toFixed(8) : 'N/A'}</td>
                          <td>{isValidNumber(exchange.blockPrice) ? exchange.blockPrice.toFixed(8) : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <span>No exchanges available</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-button"
        >
          Previous
        </button>

        {pageNumbers.slice(startPage - 1, endPage).map((number) => (
          <button
            key={number}
            onClick={() => paginate(number)}
            className={`pagination-button ${currentPage === number ? 'active' : ''}`}
          >
            {number}
          </button>
        ))}

        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === pageNumbers.length}
          className="pagination-button"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Blocks;
